/* ─────────────────────────────────────────────────────────────
   Google Sheets data source
   ─────────────────────────────────────────────────────────────

   ❗ KURULUM:
      1. Google Sheets'i açın → Dosya → Paylaş → Web'de yayınla
      2. "Tüm belge" → "Virgülle ayrılmış değerler (.csv)"
      3. "Yayınla" butonuna tıklayın
      4. SHEET_ID değerini aşağıda güncelleyin (URL'deki /d/…/edit kısmı)

   Sayfa GID'lerini bulmak için her sayfanın tab'ına tıklayın
   ve URL'nin sonundaki  #gid=XXXXXXX  değerini kullanın.
   ───────────────────────────────────────────────────────────── */

export const SHEET_ID = "10qGMu08eDRZyOhInQ20NhB-290kYd0dMMMtQF1blGw8";

// Model portföy sayfasının GID'i (admin tarafından düzenlenir, read-only)
export const MODEL_GID = "1079590890";

export const SHEET_GIDS = {
  BIST:   "0",           // BIST sayfasının gid'i  (+ USDTRY satırı içerir)
  NASDAQ: "467365338",   // NASDAQ sayfasının gid'i
  KRIPTO: "1851345953",  // KRIPTO sayfasının gid'i
  KMADEN: "1417366624",          // Apps Script çalıştırdıktan sonra gid'i buraya yazın
};

/* ─── gviz URL (CORS proxy gerekmez) ────────────────────────── */
// "Bağlantıya sahip herkes görüntüleyebilir" ayarı yeterli.
// gviz/tq endpoint'i tarayıcıdan direkt çekilebilir.
const gvizUrl = (gid) =>
  `https://docs.google.com/spreadsheets/d/${SHEET_ID}/gviz/tq?tqx=out:json&gid=${gid}`;

/* ─── Public API ─────────────────────────────────────────────── */

/**
 * Bir kategorinin Google Sheets CSV'sini çekip ayrıştırır.
 * Döndürür: { prices: { "SYM:CAT": number }, usdtry: number|null }
 */
export const fetchSheetPrices = async (category) => {
  const gid = SHEET_GIDS[category];
  if (!gid) return { prices: {}, usdtry: null };

  try {
    const res  = await fetch(gvizUrl(gid), { cache: "no-store" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const text = await res.text();
    // gviz yanıtı: /*O_o*/\ngoogle.visualization.Query.setResponse({...});
    const json = JSON.parse(text.substring(47).slice(0, -2));

    const prices = {};
    let   usdtry = null;

    // Beklenen sütunlar: A=Sembol  B=Ad  C=Fiyat (₺ veya $)
    json.table.rows.forEach((r) => {
      const symbol = String(r.c[0]?.v ?? "").trim().toUpperCase();
      const price  = r.c[2]?.v;   // sayısal değer doğrudan gelir

      if (!symbol || price == null || price <= 0) return;

      if (symbol === "USDTRY") {
        usdtry = price;
      } else {
        prices[`${symbol}:${category}`] = price;
      }
    });

    return { prices, usdtry };
  } catch (err) {
    console.warn(`[Sheets] ${category} okunamadı:`, err.message);
    return { prices: {}, usdtry: null };
  }
};

/**
 * Model Portföy sayfasını çeker (read-only, admin tarafından düzenlenir).
 * Beklenen sütunlar: A=Sembol  B=İşlem Tarihi  C=Giriş Fiyatı  D=Güncel Değer  E=Not
 * Döndürür: [{ symbol, entryDate, cost, currentPrice, notes }]
 */
export const fetchModelPortfolio = async () => {
  try {
    const res  = await fetch(gvizUrl(MODEL_GID), { cache: "no-store" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const text = await res.text();
    const json = JSON.parse(text.substring(47).slice(0, -2));

    const rows = [];

    json.table.rows.forEach((r) => {
      const symbol = String(r.c[0]?.v ?? "").trim().toUpperCase();
      if (!symbol) return;

      // Tarih: gviz "Date(YYYY,M,D)" formatında gelir (ay 0-indexed)
      let entryDate = null;
      const dateCell = r.c[1];
      if (dateCell?.f) {
        entryDate = dateCell.f;                         // zaten formatlanmış
      } else if (dateCell?.v) {
        const m = String(dateCell.v).match(/Date\((\d+),(\d+),(\d+)\)/);
        if (m) {
          const d = new Date(Number(m[1]), Number(m[2]), Number(m[3]));
          entryDate = d.toLocaleDateString("tr-TR");
        } else {
          entryDate = String(dateCell.v);
        }
      }

      const cost         = r.c[2]?.v ?? null;
      const currentPrice = r.c[3]?.v ?? null;
      const notes        = String(r.c[4]?.v ?? "").trim();

      if (!symbol || cost == null || cost <= 0) return;

      rows.push({ symbol, entryDate, cost, currentPrice: (currentPrice != null && currentPrice > 0) ? currentPrice : null, notes });
    });

    return rows;
  } catch (err) {
    console.warn("[Sheets] Model portföy okunamadı:", err.message);
    throw err;
  }
};

/**
 * Autocomplete için tüm kategorilerin sembol+ad listesini döndürür.
 * Fiyat sayfalarının A (Sembol) ve B (Ad) sütunlarını okur.
 * Döndürür: [{ symbol, name, category }]
 */
export const fetchSymbols = async () => {
  const cats = Object.entries(SHEET_GIDS).filter(([, gid]) => gid !== null);

  const results = await Promise.allSettled(
    cats.map(async ([cat, gid]) => {
      const res  = await fetch(gvizUrl(gid), { cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      const json = JSON.parse(text.substring(47).slice(0, -2));
      return json.table.rows
        .map((r) => ({
          symbol:   String(r.c[0]?.v ?? "").trim().toUpperCase(),
          name:     String(r.c[1]?.v ?? "").trim(),
          category: cat,
        }))
        .filter((s) => s.symbol && s.symbol !== "USDTRY" && s.name);
    })
  );

  return results
    .filter((r) => r.status === "fulfilled")
    .flatMap((r) => r.value);
};

/**
 * Tüm kategorileri paralel olarak çeker.
 * Döndürür: { prices, usdtry, ok }
 *   ok = en az bir kategori başarıyla yüklendiyse true
 */
export const fetchAllSheetPrices = async () => {
  const cats = Object.entries(SHEET_GIDS)
    .filter(([, gid]) => gid !== null)
    .map(([cat]) => cat);

  const results = await Promise.allSettled(
    cats.map((cat) => fetchSheetPrices(cat))
  );

  let prices = {};
  let usdtry = null;
  let ok     = false;

  results.forEach((r) => {
    if (r.status === "fulfilled") {
      const { prices: p, usdtry: rate } = r.value;
      if (Object.keys(p).length > 0) ok = true;
      prices  = { ...prices, ...p };
      usdtry  = rate ?? usdtry;
    }
  });

  return { prices, usdtry, ok };
};
